import type { Action, PubSubject } from '@_/constants';
import type { ForcedSubject } from '@casl/ability';
import { createMongoAbility } from '@casl/ability';
import { useQuery } from '@tanstack/react-query';
import { createContext, use, useMemo } from 'react';
import { getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { useStores } from './StoreContext.tsx';

interface AccessContextType {
  isReady: boolean;
  userHasAbility: (action: Action, subject: PubSubject | ForcedSubject<unknown>) => boolean;
}

const AccessContext = createContext<AccessContextType | undefined>(undefined);

export function AccessProvider({ children }: React.PropsWithChildren) {
  const { useAuthStore } = useStores();
  const isAuthenticated = useAuthStore(state => !!state.session);

  const query = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.permissions.list),
    enabled: isAuthenticated
  });

  const userAbilityRules = query.data?.data;

  const isReady = isAuthenticated || !query.isPending;

  return (
    <AccessContext
      value={useMemo(() => {
        const userAbilities = userAbilityRules
          ? createMongoAbility(userAbilityRules)
          : createMongoAbility();

        return {
          isReady,
          userHasAbility: (action, subject) => userAbilities.can(action, subject)
        };
      }, [isReady, userAbilityRules])}
    >
      {children}
    </AccessContext>
  );
}

export function useAccess() {
  const context = use(AccessContext);

  if (!context) {
    throw new Error(`useAccess needs to be used inside <AccessProvider>`);
  }

  return context;
}
